/**
 * C-09 取引先の単票（T-D8・05 §6.1）＝1件の得意先を、この画面だけで立ち上げられるようにする。
 *
 * 中身は3つ：**基本情報**（掛率・支払条件・FAX・アプリ発注）／**現場**（FR-12）／
 * **個別単価**（FR-14 の第3層）。加えて招待コード発行への導線（FR-05＝05 §6.1 C-09 が
 * 「得意先詳細から招待コード発行導線」と定めている）。
 *
 * 認可（03 §7 マスタ行）：**新規作成は admin・更新は staff も可**。
 * backend の POST=ConsoleAdmin / PATCH=ConsoleUser にそのまま合わせる。
 */

import { auth, console_, type Site } from "@kenzai/api-client";
import { Badge, Breadcrumb, Card, ConfirmDialog, Empty, ErrorNote, PageHeader, Spinner, Table, Td } from "@kenzai/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { Connections } from "./Connections";
import { CustomerPrices } from "./CustomerPrices";
import { InviteCodes } from "./InviteCodes";
import { TabButton } from "./Masters";
import { CustomerForm, SiteForm } from "./masterForms";
import { hintStyle } from "./formStyles";

export function CustomerDetail() {
  const { customerId: raw } = useParams();
  const customerId = Number(raw);
  const qc = useQueryClient();

  const me = useQuery({ queryKey: ["me"], queryFn: () => auth.me(), staleTime: Infinity });
  const isAdmin = me.data?.role === "admin";
  const customer = useQuery({
    queryKey: ["customer", customerId],
    queryFn: () => console_.customer(customerId),
    enabled: Number.isFinite(customerId),
  });

  const [params, setParams] = useSearchParams();
  const tabParam = params.get("tab") ?? "";
  const tab: "" | "invite-codes" | "connections" =
    tabParam === "invite-codes" || tabParam === "connections" ? tabParam : "";
  const [editing, setEditing] = useState(false);
  const [toggling, setToggling] = useState(false);

  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) => console_.patchCustomer(customerId, body),
    onSettled: () => {
      setToggling(false);
      qc.invalidateQueries({ queryKey: ["customer", customerId] });
      qc.invalidateQueries({ queryKey: ["customers"] });
    },
  });

  if (!Number.isFinite(customerId)) return <ErrorNote error={{ message: "取引先が見つかりません。" }} />;
  if (customer.isLoading) return <Spinner />;
  if (customer.isError) return <ErrorNote error={customer.error} />;
  const c = customer.data!;

  return (
    <>
      {/* U3 パンくず＝いまどこにいて、どこへ戻れるか（単発の「← 戻る」を各画面が持たない） */}
      <Breadcrumb
        linkAs={Link}
        items={[{ label: "取引先・現場", to: "/masters/customers" }, { label: c.name }]}
      />
      <PageHeader
        title={c.name}
        subtitle={c.display_no ?? undefined}
        actions={
          <>
            <button type="button" data-testid="customer-edit" onClick={() => setEditing(true)} className="kz-btn">
              編集
            </button>
            {/* 利用停止は**更新**（PATCH status）＝staff も可（backend が ConsoleUser・
                配送便 C-18 も同じ扱い）。ここだけ admin に閉じると、同じ操作なのに
                画面ごとに可否が違う状態になる（レビュー確定分の不揃い） */}
            <button
              type="button"
              data-testid="customer-toggle"
              onClick={() => setToggling(true)}
              className="kz-btn"
            >
              {c.status === "active" ? "利用停止にする" : "利用を再開する"}
            </button>
          </>
        }
      />
      {patch.isError && <ErrorNote error={patch.error} />}

      {/* ★D-79：取引先を最上位に置き、**招待コード・接続はこの下のタブ**にした。
          以前はサイドバーの「設定・管理」に独立項目として並んでいて、
          「この取引先は誰とつながっているか」を見るのに別の場所へ行く必要があった。 */}
      {/* ★外側の flex は「タブの器を左に寄せる」ため＝`.kz-tabs` は inline-flex なので、
          直接ここに置くと行ボックスができて下に余分な余白が出る。 */}
      <div style={{ display: "flex", marginBottom: "var(--kz-space-4)" }}>
        <div className="kz-tabs">
          {(
            [
              ["", "基本情報・現場・単価"],
              ["invite-codes", "招待コード"],
              ["connections", "接続（アプリ利用者）"],
            ] as const
          ).map(([value, label]) => (
            <TabButton
              key={value || "base"}
              active={tab === value}
              testid={`customer-tab-${value || "base"}`}
              onClick={() => setParams(value ? { tab: value } : {})}
            >
              {label}
            </TabButton>
          ))}
        </div>
      </div>

      {tab === "invite-codes" && <InviteCodes customerId={c.id} />}
      {tab === "connections" && <Connections customerId={c.id} />}

      <div style={{ display: tab === "" ? "grid" : "none", gap: "var(--kz-space-4)" }}>
        <Card>
          <SectionTitle>基本情報</SectionTitle>
          <dl style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "var(--kz-space-2) var(--kz-space-4)", margin: 0 }}>
            <Row label="状態">
              <span data-testid="customer-status">
                {c.status === "active" ? <Badge tone="primary">利用中</Badge> : <Badge>利用停止</Badge>}
              </span>
            </Row>
            <Row label="担当者">{c.person ?? "—"}</Row>
            <Row label="FAX番号">
              <span className="kz-num">{c.fax_number ?? "—"}</span>
            </Row>
            <Row label="掛率">
              <span className="kz-num" data-testid="customer-kake-value">
                {c.kake_rate ?? "—"}
              </span>
            </Row>
            <Row label="支払条件">{c.payment_terms ?? "—"}</Row>
            <Row label="アプリ発注">{c.line_enabled ? <Badge tone="primary">あり</Badge> : <Badge>—</Badge>}</Row>
            <Row label="備考">{c.note ?? "—"}</Row>
          </dl>
        </Card>

        <Card>
          <Sites customerId={c.id} isAdmin={isAdmin} />
        </Card>

        <Card>
          <SectionTitle>個別単価</SectionTitle>
          <CustomerPrices customer={c} isAdmin={isAdmin} />
        </Card>
      </div>

      {editing && (
        <CustomerForm initial={c} onDone={() => setEditing(false)} onCancel={() => setEditing(false)} />
      )}
      {toggling && (
        <ConfirmDialog
          title={c.status === "active" ? "この取引先を利用停止にしますか？" : "この取引先の利用を再開しますか？"}
          body={
            c.status === "active" ? (
              <>
                一覧の既定の表示から外れます。過去の受注・帳票はそのまま残ります。
                <br />
                <strong>
                  すでに接続している取引先アプリからの注文は、これだけでは止まりません。
                </strong>
                {/* 行き先（C-20）は画面上部の「接続」ボタン＝**ダイアログの中にリンクを置かない**
                    （押した瞬間にこの利用停止の操作が黙って捨てられる）。T-D24 */}
                止めるには招待コードを失効させ、このあと上の「接続（アプリ利用者）」タブから解除してください。
              </>
            ) : (
              "一覧の既定の表示に戻ります。"
            )
          }
          confirmLabel={c.status === "active" ? "利用停止にする" : "利用を再開する"}
          tone={c.status === "active" ? "danger" : "primary"}
          busy={patch.isPending}
          onConfirm={() =>
            patch.mutate({
              status: c.status === "active" ? "disabled" : "active",
              updated_at: c.updated_at,
            })
          }
          onCancel={() => setToggling(false)}
        />
      )}
    </>
  );
}

function SectionTitle({ children }: { children: ReactNode }) {
  return <h2 style={{ margin: "0 0 var(--kz-space-3)", fontSize: "var(--kz-font-size-lg)" }}>{children}</h2>;
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt style={{ color: "var(--kz-color-ink-muted)", fontSize: "var(--kz-font-size-sm)" }}>{label}</dt>
      <dd style={{ margin: 0 }}>{children}</dd>
    </>
  );
}

/**
 * 現場（FR-12）。**利用停止も出す**（管理画面＝止めたものを戻せる必要がある）。
 *
 * 認可（03 §7）：**新規は admin のみ**（backend の POST が ConsoleAdmin）・編集と利用停止は staff も可。
 * 押しても 403 になるだけのボタンは出さない（Catalogs.tsx と同じ作法）。
 */
function Sites({ customerId, isAdmin }: { customerId: number; isAdmin: boolean }) {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["sites", customerId],
    queryFn: () => console_.sites(customerId, true),
  });
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Site | null>(null);
  const [toggling, setToggling] = useState<Site | null>(null);

  const patch = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Record<string, unknown> }) => console_.patchSite(id, body),
    onSettled: () => {
      setToggling(null);
      qc.invalidateQueries({ queryKey: ["sites", customerId] });
    },
  });

  const rows = list.data ?? [];

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", marginBottom: "var(--kz-space-3)" }}>
        <SectionTitle>現場</SectionTitle>
        {isAdmin && (
          <button
            type="button"
            data-testid="site-new"
            onClick={() => setCreating(true)}
            className="kz-btn kz-btn--primary"
            style={{ marginLeft: "auto", marginBottom: "var(--kz-space-3)" }}
          >
            現場を追加
          </button>
        )}
      </div>
      {patch.isError && <ErrorNote error={patch.error} />}
      {list.isLoading ? (
        <Spinner />
      ) : list.isError ? (
        <ErrorNote error={list.error} />
      ) : rows.length === 0 ? (
        <Empty
          title="現場がありません"
          hint={
            isAdmin
              ? "「現場を追加」から登録すると、取引先が注文するときの納品先として選べます。"
              : "管理者に登録を依頼してください（取引先は注文時に「その他」で住所を直接入力できます）。"
          }
        />
      ) : (
        <Card padding="0">
          <Table head={["現場", "納品先住所", "状態", "操作"]}>
            {rows.map((s) => (
              <tr key={s.id} data-testid={`site-${s.id}`}>
                <Td style={{ fontWeight: 600 }}>{s.name}</Td>
                <Td>{s.address ?? "—"}</Td>
                <Td>
                  {/* ボタン文言（「利用停止にする」）と同じ語が行内にあるので、状態は testid で見る
                      ＝`getByText("利用停止")` だと**停止に失敗しても通る**空振りになる（S4 と同型） */}
                  <span data-testid={`site-status-${s.id}`}>
                    {s.status === "active" ? <Badge tone="primary">利用中</Badge> : <Badge>利用停止</Badge>}
                  </span>
                </Td>
                <Td>
                  <div style={{ display: "flex", gap: "var(--kz-space-2)" }}>
                    <button
                      type="button"
                      data-testid={`site-edit-${s.id}`}
                      onClick={() => setEditing(s)}
                      className="kz-btn kz-btn--sm"
                    >
                      編集
                    </button>
                    <button
                      type="button"
                      data-testid={`site-toggle-${s.id}`}
                      onClick={() => setToggling(s)}
                      className="kz-btn kz-btn--sm"
                    >
                      {s.status === "active" ? "利用停止にする" : "利用を再開する"}
                    </button>
                  </div>
                </Td>
              </tr>
            ))}
          </Table>
        </Card>
      )}
      <p style={{ ...hintStyle, marginTop: "var(--kz-space-3)", marginBottom: 0 }}>
        現場は取引先の注文画面（納品先）に出ます。一覧にない現場は、取引先が「その他」で住所を直接入力できます。
      </p>

      {creating && (
        <SiteForm customerId={customerId} onDone={() => setCreating(false)} onCancel={() => setCreating(false)} />
      )}
      {editing && (
        <SiteForm
          customerId={customerId}
          initial={editing}
          onDone={() => setEditing(null)}
          onCancel={() => setEditing(null)}
        />
      )}
      {toggling && (
        <ConfirmDialog
          title={
            toggling.status === "active" ? "この現場を利用停止にしますか？" : "この現場の利用を再開しますか？"
          }
          body={
            toggling.status === "active"
              ? "取引先の注文画面の納品先の選択肢から消えます。すでに入っている注文はそのまま残ります。"
              : "取引先の注文画面の納品先の選択肢に戻ります。"
          }
          confirmLabel={toggling.status === "active" ? "利用停止にする" : "利用を再開する"}
          busy={patch.isPending}
          onConfirm={() =>
            patch.mutate({
              id: toggling.id,
              body: {
                status: toggling.status === "active" ? "disabled" : "active",
                updated_at: toggling.updated_at,
              },
            })
          }
          onCancel={() => setToggling(null)}
        />
      )}
    </>
  );
}
